
import { useState, FormEvent } from 'react';

export default function PromoClaimForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    interest: 'Flash Deal — Professional Annual',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) return;
    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));

      await fetch('https://readdy.ai/api/form/d6akpvea728k8ctu3c7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', business: '', interest: 'Flash Deal — Professional Annual', message: '' });
    } catch {
      // Silently handle
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="claim-form" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
            Claim Your Deal
          </h2>
          <p className="text-slate-400 max-w-lg mx-auto">
            Not sure which deal is right for you? Fill in your details and our team will reach out with a personalised recommendation.
          </p>
        </div>

        {isSubmitted ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-3xl text-emerald-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Request Received!</h3>
            <p className="text-slate-400 text-sm">We&apos;ll be in touch within 2 business hours with your personalised deal.</p>
          </div>
        ) : (
          <form
            id="promo-deal-claim-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-10 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1.5">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Jake Morrison"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1.5">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="jake@example.com"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1.5">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="04XX XXX XXX"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1.5">Business Name</label>
                <input
                  type="text"
                  name="business"
                  value={formData.business}
                  onChange={handleChange}
                  placeholder="Your business name"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">Interested In</label>
              <select
                name="interest"
                value={formData.interest}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 text-sm cursor-pointer"
              >
                <option>Flash Deal — Professional Annual</option>
                <option>Flash Deal — Enterprise Annual</option>
                <option>Flash Deal — Professional 6 Months</option>
                <option>Tradie Starter Bundle</option>
                <option>Team Growth Bundle</option>
                <option>Full Business Suite</option>
                <option>Promo Code Help</option>
                <option>Not sure — need advice</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">Message (optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                placeholder="Tell us about your team size, trade, or any questions..."
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm resize-none"
              />
              <p className="text-slate-600 text-xs mt-1 text-right">{formData.message.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-amber-500/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {isSubmitting ? 'Submitting...' : 'Get My Personalised Deal'}
            </button>

            <p className="text-slate-500 text-xs text-center">
              <i className="ri-lock-line mr-1"></i>
              Your information is secure and will never be shared.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
